/**
 * The generic order flow (04-architecture § 8.5), shared by every step of the
 * funnel. The rules that matter and are easy to get wrong:
 *
 *  - the idempotency key belongs to the **intent**, not to the request; it is
 *    reused for every retry and replaced only when the body changes or the
 *    order reaches a terminal state;
 *  - a declined card does **not** fail the order — the user may try another one
 *    until the hold expires;
 *  - the Stripe SDK's success is never final: the webhook is, so we poll
 *    `GET /orders/{id}` until it says `paid`.
 */

import type { components, paths } from '@cowoo/api-client';
import { newIdempotencyKey } from '../api/client.js';

/**
 * An order item exactly as the API defines it. Derived from the generated
 * client rather than restated, so a new item type on the server cannot silently
 * diverge from what the apps are allowed to build (D-006).
 */
export type OrderItemDraft = components['schemas']['OrderItemInput'];

/**
 * § 5.7 — the item types the server **requires** `earlyPerformanceConsent` for
 * (server 07 § 4.2). A contract is deliberately not among them: whether an
 * immediate start needs an equivalent line is legal open item OI-L-02, and the
 * API has no field for it, so nothing is shown.
 */
export const EARLY_PERFORMANCE_ITEMS = ['day_ticket', 'guest_ticket', 'meeting_block'] as const;

export type OrderStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'cancelled';

/** § 8.5 step 5 — 1 s for the first 30 s, then 3 s up to two minutes. */
export const POLL_FAST_MS = 1000;
export const POLL_FAST_UNTIL_MS = 30_000;
export const POLL_SLOW_MS = 3000;
export const POLL_TIMEOUT_MS = 120_000;

export function pollDelay(elapsedMs: number): number {
  return elapsedMs < POLL_FAST_UNTIL_MS ? POLL_FAST_MS : POLL_SLOW_MS;
}

export interface PollResult {
  status: OrderStatus | 'timeout';
  lastPaymentError?: string | null;
  invoiceId?: string | null;
}

/** What the poller needs from the API; injected so this module stays pure. */
export type FetchOrder = (orderId: string) => Promise<{
  status: OrderStatus;
  lastPaymentError?: string | null;
  invoiceId?: string | null;
} | null>;

/**
 * Polls until the order reaches a terminal state. SSE `booking`/`account`
 * events short-circuit this by invalidating the query; the poll is the floor,
 * not the only channel.
 */
export async function pollOrder(
  orderId: string,
  fetchOrder: FetchOrder,
  now: () => number = Date.now,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<PollResult> {
  const startedAt = now();

  for (;;) {
    const elapsed = now() - startedAt;
    if (elapsed > POLL_TIMEOUT_MS) return { status: 'timeout' };

    const order = await fetchOrder(orderId);
    if (order && order.status !== 'pending') {
      return {
        status: order.status,
        lastPaymentError: order.lastPaymentError,
        invoiceId: order.invoiceId,
      };
    }
    await sleep(pollDelay(elapsed));
  }
}

/** A new key is generated only when the body changes or after a terminal result. */
export function keyForItems(
  items: OrderItemDraft[],
  previous: { key: string; hash: string } | null,
  /**
   * Anything else that changes the order body for the same items — the list
   * price after `trial_not_eligible`. The server refuses a reused key with a
   * different body (`idempotency_conflict`).
   */
  variant = '',
): {
  key: string;
  hash: string;
} {
  const hash = `${JSON.stringify(items)}${variant ? `|${variant}` : ''}`;
  if (previous && previous.hash === hash) return previous;
  return { key: newIdempotencyKey(), hash };
}

/** § 8.5 step 2 — early-performance consent is mandatory for these item types. */
export function needsEarlyPerformanceConsent(items: readonly OrderItemDraft[]): boolean {
  return items.some((item) => (EARLY_PERFORMANCE_ITEMS as readonly string[]).includes(item.type));
}

/**
 * § 5.7 — the CTA may only enable once every **shown** box is ticked, and the
 * body must never claim a consent that was not shown.
 */
export function consentsSatisfied(
  items: readonly OrderItemDraft[],
  state: { acceptedTerms: boolean; earlyPerformance: boolean },
): boolean {
  if (!state.acceptedTerms) return false;
  return needsEarlyPerformanceConsent(items) ? state.earlyPerformance : true;
}

/**
 * The `POST /orders` body. Derived from the path rather than a named schema:
 * the server registers a name for the *response*, so this is the only spelling
 * that cannot drift from what the endpoint actually accepts.
 */
export type OrderBody = NonNullable<paths['/orders']['post']['requestBody']>['content']['application/json'];

/**
 * The POST body for an order whose consents are satisfied. Returns `null` when
 * they are not, so a caller cannot construct a body that claims a consent the
 * user never gave — the type says `acceptedTerms: true`, and this is the only
 * place that can produce it.
 */
export function orderBody(
  items: OrderItemDraft[],
  state: { acceptedTerms: boolean; earlyPerformance: boolean },
  extra: { channel: 'app' | 'web'; stripeApiVersion?: string; useTrial?: boolean },
): OrderBody | null {
  if (!consentsSatisfied(items, state)) return null;
  return {
    items,
    acceptedTerms: true,
    // Never sent for a box that was not shown and ticked.
    ...(needsEarlyPerformanceConsent(items) ? { earlyPerformanceConsent: true as const } : {}),
    ...extra,
  };
}

export * from './summary.js';
